import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../api/axios';
import { format, isToday, isThisWeek, isThisMonth, isThisYear } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { useAuth } from '../contexts/AuthContext';
import { pagamentoEmAberto } from '../utils/pagamentos';

const PERIODOS = [
  { value: '',       label: 'Todos' },
  { value: 'hoje',   label: 'Hoje' },
  { value: 'semana', label: 'Semana' },
];

const PERIODOS_ADMIN = [
  { value: 'hoje',   label: 'Hoje' },
  { value: 'semana', label: 'Semana' },
  { value: 'mes',    label: 'Mês' },
];

function dentroDoperiodo(servico, periodo) {
  if (!periodo) return true;
  const data = new Date(servico.updatedAt || servico.createdAt);
  if (periodo === 'hoje')   return isToday(data);
  if (periodo === 'semana') return isThisWeek(data, { weekStartsOn: 0 });
  if (periodo === 'mes')    return isThisMonth(data);
  if (periodo === 'ano')    return isThisYear(data);
  return true;
}

const STATUS = {
  orcamento:            { label: 'Ag. Avaliação do Mecânico', cor: 'bg-gray-100 text-gray-700' },
  aprovado:             { label: 'Aprovado - ag. mecânico', cor: 'bg-blue-100 text-blue-800' },
  em_andamento:         { label: 'Em andamento/Em conserto', cor: 'bg-orange-100 text-orange-800' },
  aguardando_orcamento: { label: 'Enviar orçamento p/ aprovação do cliente', cor: 'bg-purple-100 text-purple-800' },
  aguardando_cliente:   { label: 'Aguardando aprovação do cliente', cor: 'bg-cyan-100 text-cyan-800' },
  concluido:            { label: 'Concluído',              cor: 'bg-green-100 text-green-800' },
  cancelado:            { label: 'Orçamento Não Aprovado', cor: 'bg-red-100 text-red-800' },
};

const STATUS_ATIVOS = ['orcamento', 'aguardando_orcamento', 'aprovado', 'em_andamento'];

function statusPagamento(s) {
  const totalPago = (s.pagamentos || []).reduce((a, p) => a + (p.valor || 0), 0);
  const totalOS = s.totalGeral || 0;
  if (totalPago === 0) return 'pendente';
  if (totalPago >= totalOS) return 'pago';
  return 'parcial';
}

function CardOS({ s, onClick, isAdmin }) {
  return (
    <div
      onClick={onClick}
      className="bg-white rounded-xl shadow-sm p-4 cursor-pointer hover:shadow-md active:scale-95 transition-all border border-gray-100"
    >
      <div className="flex items-center justify-between mb-3">
        <span className="font-bold text-gray-800">OS #{s.numero}</span>
        <span className={`text-xs font-semibold px-2.5 py-1 rounded-full ${STATUS[s.status]?.cor}`}>
          {STATUS[s.status]?.label}
        </span>
      </div>
      <div className="flex items-center gap-2 mb-1">
        <span className="text-lg">👤</span>
        <span className="font-medium text-gray-700 truncate">{s.cliente?.nome}</span>
      </div>
      <div className="flex items-center gap-2 mb-1">
        <span className="text-lg">🚗</span>
        <span className="text-sm text-gray-500">{s.veiculo?.placa} • {s.veiculo?.marca} {s.veiculo?.modelo}</span>
      </div>
      {s.responsavel && (
        <div className="flex items-center gap-2 mb-3">
          <span className="text-lg">🔧</span>
          <span className="text-xs font-medium text-blue-600">{s.responsavel}</span>
        </div>
      )}
      {!isAdmin && s.status === 'aguardando_orcamento' && (
        <div className="bg-purple-50 border border-purple-100 rounded-lg px-3 py-1.5 mb-2 text-xs text-purple-700 font-medium">
          ⏳ Orçamento enviado — aguardando aprovação do administrativo
        </div>
      )}
      {!isAdmin && s.status === 'aguardando_cliente' && (
        <div className="bg-cyan-50 border border-cyan-100 rounded-lg px-3 py-1.5 mb-2 text-xs text-cyan-700 font-medium">
          ⏳ Aguardando aprovação do cliente
        </div>
      )}
      {isAdmin && s.status === 'concluido' && (() => {
        const sp = statusPagamento(s);
        if (sp === 'pago') {
          return (
            <div className="bg-green-50 border border-green-300 rounded-lg px-3 py-1.5 mb-2 text-xs text-green-700 font-semibold flex items-center gap-1.5">
              ✅ Pago — OS quitada
            </div>
          );
        }
        if (sp === 'parcial') {
          return (
            <div className="bg-yellow-50 border border-yellow-300 rounded-lg px-3 py-1.5 mb-2 text-xs text-yellow-700 font-semibold flex items-center gap-1.5">
              ⚠️ Pagamento parcial
            </div>
          );
        }
        return (
          <div className="bg-orange-50 border border-orange-300 rounded-lg px-3 py-1.5 mb-2 text-xs text-orange-700 font-semibold flex items-center gap-1.5">
            💰 Pagamento não registrado
          </div>
        );
      })()}
      <div className="flex items-center justify-between border-t border-gray-50 pt-2 mt-1">
        <span className="text-xs text-gray-400">
          {format(new Date(s.updatedAt || s.dataEntrada), "dd/MM/yyyy 'às' HH:mm", { locale: ptBR })}
        </span>
        {isAdmin && (
          <span className="text-sm font-bold text-gray-800">R$ {s.totalGeral?.toFixed(2)}</span>
        )}
      </div>
    </div>
  );
}

function ListaVazia({ mensagem }) {
  return (
    <div className="bg-white rounded-xl shadow-sm p-10 text-center text-gray-400">
      <div className="text-4xl mb-2">🔧</div>
      <p>{mensagem}</p>
    </div>
  );
}

// ── Painel do mecânico ──────────────────────────────────────────────────────
function DashboardMecanico({ usuario }) {
  const navigate = useNavigate();
  const [aba, setAba] = useState('meus');
  const [servicos, setServicos] = useState([]);
  const [carregando, setCarregando] = useState(true);

  useEffect(() => {
    api.get('/servicos').then(r => {
      setServicos(r.data);
      setCarregando(false);
    });
  }, []);

  // A API já filtra só as OSs do mecânico logado
  const emAberto = servicos.filter(s => ['orcamento', 'aprovado', 'aguardando_orcamento', 'aguardando_cliente'].includes(s.status));
  const meusServicos = servicos.filter(s => s.status === 'em_andamento');
  const finalizados = servicos.filter(s => ['concluido', 'cancelado'].includes(s.status));

  if (carregando) return <div className="flex items-center justify-center h-64 text-gray-500">Carregando...</div>;

  return (
    <div className="max-w-4xl mx-auto">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold text-gray-800">Painel</h1>
        <button
          onClick={() => navigate('/servicos/novo')}
          className="bg-blue-600 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-blue-700"
        >
          + Nova OS
        </button>
      </div>

      {/* Abas */}
      <div className="flex gap-1 bg-gray-100 p-1 rounded-xl mb-5">
        <button onClick={() => setAba('abertos')}
          className={`flex-1 py-2.5 rounded-lg text-sm font-semibold transition-colors ${aba === 'abertos' ? 'bg-white text-gray-800 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}>
          Em Aberto
          {emAberto.length > 0 && <span className="ml-2 bg-blue-600 text-white text-xs font-bold px-2 py-0.5 rounded-full">{emAberto.length}</span>}
        </button>
        <button onClick={() => setAba('meus')}
          className={`flex-1 py-2.5 rounded-lg text-sm font-semibold transition-colors ${aba === 'meus' ? 'bg-white text-gray-800 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}>
          Em Andamento
          {meusServicos.length > 0 && <span className="ml-2 bg-orange-500 text-white text-xs font-bold px-2 py-0.5 rounded-full">{meusServicos.length}</span>}
        </button>
        <button onClick={() => setAba('finalizados')}
          className={`flex-1 py-2.5 rounded-lg text-sm font-semibold transition-colors ${aba === 'finalizados' ? 'bg-white text-gray-800 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}>
          Finalizados
          {finalizados.length > 0 && <span className="ml-2 bg-gray-400 text-white text-xs font-bold px-2 py-0.5 rounded-full">{finalizados.length}</span>}
        </button>
      </div>

      {aba === 'abertos' && (
        <>
          <p className="text-sm text-gray-400 mb-3">OS aguardando início ou aprovação</p>
          {emAberto.length === 0
            ? <ListaVazia mensagem="Nenhum serviço em aberto no momento" />
            : <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {emAberto.map(s => <CardOS key={s._id} s={s} onClick={() => navigate(`/servicos/${s._id}`, { state: { from: '/' } })} isAdmin={false} />)}
              </div>
          }
        </>
      )}
      {aba === 'meus' && (
        <>
          <p className="text-sm text-gray-400 mb-3">OS em andamento sob sua responsabilidade</p>
          {meusServicos.length === 0
            ? <ListaVazia mensagem="Você não possui serviços em andamento" />
            : <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {meusServicos.map(s => <CardOS key={s._id} s={s} onClick={() => navigate(`/servicos/${s._id}`, { state: { from: '/' } })} isAdmin={false} />)}
              </div>
          }
        </>
      )}
      {aba === 'finalizados' && (
        <>
          <p className="text-sm text-gray-400 mb-3">OS concluídas ou com orçamento não aprovado</p>
          {finalizados.length === 0
            ? <ListaVazia mensagem="Nenhuma OS finalizada" />
            : <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {finalizados.map(s => (
                  <div key={s._id} className="opacity-60 pointer-events-none select-none">
                    <CardOS s={s} onClick={() => {}} isAdmin={false} />
                  </div>
                ))}
              </div>
          }
        </>
      )}
    </div>
  );
}

const MESES = ['Janeiro','Fevereiro','Março','Abril','Maio','Junho','Julho','Agosto','Setembro','Outubro','Novembro','Dezembro'];

// ── Painel do admin ─────────────────────────────────────────────────────────
function DashboardAdmin() {
  const { isProprietario } = useAuth();
  const [dados, setDados] = useState(null);
  const [servicos, setServicos] = useState([]);
  const navigate = useNavigate();
  const hoje = new Date();
  const [mes, setMes] = useState(hoje.getMonth());
  const [ano, setAno] = useState(hoje.getFullYear());
  const [filtroPeriodo, setFiltroPeriodo] = useState('hoje');
  const [filtroStatus, setFiltroStatus] = useState('');
  const [filtroSemPagamento, setFiltroSemPagamento] = useState(false);
  const [busca, setBusca] = useState('');
  const [visualizacao, setVisualizacao] = useState(localStorage.getItem('painel_view') || 'lista');
  const [colunas, setColunas] = useState(Number(localStorage.getItem('painel_colunas')) || 2);

  const alterarVisualizacao = modo => { setVisualizacao(modo); localStorage.setItem('painel_view', modo); };
  const alterarColunas = n => { setColunas(n); localStorage.setItem('painel_colunas', n); };
  const gridClass = { 1: 'grid-cols-1', 2: 'grid-cols-1 sm:grid-cols-2', 3: 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-3' }[colunas];

  useEffect(() => {
    const params = mes === -1 ? {} : { mes, ano };
    api.get('/servicos/dashboard', { params }).then(r => setDados(r.data));
  }, [mes, ano]);

  useEffect(() => {
    api.get('/servicos').then(r => setServicos(r.data));
  }, []);

  const anosDisponiveis = Array.from({ length: 5 }, (_, i) => hoje.getFullYear() - i);

  const servicosPorPeriodo = servicos.filter(s => {
    if (!dentroDoperiodo(s, filtroPeriodo)) return false;
    if (mes !== -1) {
      const d = new Date(s.updatedAt || s.createdAt);
      if (d.getMonth() !== mes || d.getFullYear() !== ano) return false;
    }
    return true;
  });
  const termo = busca.toLowerCase().trim();
  const qtdSemPagamento = servicosPorPeriodo.filter(pagamentoEmAberto).length;

  const servicosFiltrados = servicosPorPeriodo
    .filter(s => !filtroStatus || s.status === filtroStatus)
    .filter(s => !filtroSemPagamento || pagamentoEmAberto(s))
    .filter(s => !termo || (
      s.cliente?.nome?.toLowerCase().includes(termo) ||
      s.veiculo?.placa?.toLowerCase().includes(termo) ||
      s.veiculo?.modelo?.toLowerCase().includes(termo) ||
      s.veiculo?.marca?.toLowerCase().includes(termo) ||
      String(s.numero).includes(termo) ||
      s.diagnostico?.toLowerCase().includes(termo)
    ));

  const cards = dados ? (isProprietario ? [
    // Proprietário: fluxo simplificado, sem etapas de aprovação interna
    { label: 'Orçamentos em elaboração', valor: dados.orcamentos,          icon: '📝', status: 'orcamento',            cor: 'text-gray-600' },
    { label: 'Aguardando aprovação do cliente', valor: dados.aguardandoCliente, icon: '⏳', status: 'aguardando_cliente', cor: 'text-cyan-600' },
    { label: 'Em andamento/Em conserto', valor: dados.emAndamento,         icon: '🔧', status: 'em_andamento',         cor: 'text-orange-600' },
    { label: 'Concluídos',               valor: dados.concluidos,          icon: '🏁', status: 'concluido',            cor: 'text-green-600' },
    { label: 'Orçamento Não Aprovado',   valor: dados.cancelados,          icon: '❌', status: 'cancelado',            cor: 'text-red-500' },
  ] : [
    { label: 'Ag. Avaliação do Mecânico', valor: dados.orcamentos,          icon: '🔍', status: 'orcamento',            cor: 'text-gray-600' },
    { label: 'Enviar orçamento p/ aprovação do cliente', valor: dados.aguardandoOrcamento, icon: '📤', status: 'aguardando_orcamento', cor: 'text-purple-600' },
    { label: 'Aprovados - ag. mecânico',  valor: dados.abertos,             icon: '✅', status: 'aprovado',             cor: 'text-blue-600' },
    { label: 'Em andamento/Em conserto', valor: dados.emAndamento,         icon: '🔧', status: 'em_andamento',         cor: 'text-orange-600' },
    { label: 'Aguardando aprovação do cliente', valor: dados.aguardandoCliente, icon: '⏳', status: 'aguardando_cliente', cor: 'text-cyan-600' },
    { label: 'Concluídos',               valor: dados.concluidos,          icon: '🏁', status: 'concluido',            cor: 'text-green-600' },
    { label: 'Orçamento Não Aprovado',   valor: dados.cancelados,          icon: '❌', status: 'cancelado',            cor: 'text-red-500' },
  ]) : [];

  return (
    <div className="max-w-5xl mx-auto">
      {/* Cabeçalho */}
      <div className="flex items-center justify-between mb-5 gap-3">
        <h1 className="text-2xl font-bold text-gray-800">Painel</h1>
        <div className="flex items-center gap-2 flex-wrap">
          <div className="flex bg-gray-100 rounded-lg p-1 gap-1">
            {PERIODOS_ADMIN.map(({ value, label }) => (
              <button key={value} onClick={() => {
                if (filtroPeriodo === value) {
                  setFiltroPeriodo('');
                } else {
                  setFiltroPeriodo(value);
                  setMes(hoje.getMonth());
                  setAno(hoje.getFullYear());
                }
              }}
                className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${filtroPeriodo === value ? 'bg-white shadow-sm text-gray-800' : 'text-gray-500 hover:text-gray-700'}`}>
                {label}
              </button>
            ))}
          </div>
          <select value={mes} onChange={e => setMes(Number(e.target.value))}
            className="border border-gray-200 rounded-lg px-3 py-2 text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white">
            <option value={-1}>Todos os meses</option>
            {MESES.map((m, i) => <option key={i} value={i}>{m}</option>)}
          </select>
          <select value={ano} onChange={e => setAno(Number(e.target.value))}
            className="border border-gray-200 rounded-lg px-3 py-2 text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white">
            {anosDisponiveis.map(a => <option key={a} value={a}>{a}</option>)}
          </select>
          {/* Seletor de colunas — só no modo cards */}
          {visualizacao === 'cards' && (
            <div className="flex bg-gray-100 rounded-lg p-1 gap-1">
              {[1, 2, 3].map(n => (
                <button key={n} onClick={() => alterarColunas(n)} title={`${n} coluna${n > 1 ? 's' : ''}`}
                  className={`w-8 h-8 rounded-md text-xs font-bold transition-colors ${colunas === n ? 'bg-white shadow-sm text-blue-600' : 'text-gray-400 hover:text-gray-600'}`}>
                  {n}
                </button>
              ))}
            </div>
          )}
          {/* Toggle lista / cards */}
          <div className="flex bg-gray-100 rounded-lg p-1 gap-1">
            <button onClick={() => alterarVisualizacao('lista')} title="Lista"
              className={`p-2 rounded-md transition-colors ${visualizacao === 'lista' ? 'bg-white shadow-sm text-blue-600' : 'text-gray-400 hover:text-gray-600'}`}>
              <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M4 6h16M4 10h16M4 14h16M4 18h16" />
              </svg>
            </button>
            <button onClick={() => alterarVisualizacao('cards')} title="Cards"
              className={`p-2 rounded-md transition-colors ${visualizacao === 'cards' ? 'bg-white shadow-sm text-blue-600' : 'text-gray-400 hover:text-gray-600'}`}>
              <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M4 5a1 1 0 011-1h4a1 1 0 011 1v4a1 1 0 01-1 1H5a1 1 0 01-1-1V5zM14 5a1 1 0 011-1h4a1 1 0 011 1v4a1 1 0 01-1 1h-4a1 1 0 01-1-1V5zM4 15a1 1 0 011-1h4a1 1 0 011 1v4a1 1 0 01-1 1H5a1 1 0 01-1-1v-4zM14 15a1 1 0 011-1h4a1 1 0 011 1v4a1 1 0 01-1 1h-4a1 1 0 01-1-1v-4z" />
              </svg>
            </button>
          </div>
          <button onClick={() => navigate('/servicos/novo')}
            className="bg-blue-600 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-blue-700">
            + Novo Serviço
          </button>
        </div>
      </div>

      {/* Cards de contadores */}
      {dados && (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2 mb-5">
          {cards.map(card => (
            <div key={card.label}
              onClick={() => { setFiltroStatus(card.status); setFiltroSemPagamento(false); setFiltroPeriodo(''); }}
              className={`bg-white rounded-lg shadow-sm p-2 flex flex-col items-center gap-0.5 cursor-pointer hover:shadow-md transition-all active:scale-95 ${filtroStatus === card.status && !filtroSemPagamento ? 'ring-2 ring-blue-500' : ''}`}>
              <span className="text-base">{card.icon}</span>
              <span className={`text-lg font-bold ${card.cor}`}>{card.valor}</span>
              <span className="text-xs text-gray-500 text-center leading-tight">{card.label}</span>
            </div>
          ))}
          <div
            onClick={() => { setFiltroSemPagamento(true); setFiltroStatus(''); setFiltroPeriodo(''); }}
            className={`bg-white rounded-lg shadow-sm p-2 flex flex-col items-center gap-0.5 cursor-pointer hover:shadow-md transition-all active:scale-95 ${filtroSemPagamento ? 'ring-2 ring-red-400' : ''}`}>
            <span className="text-base">💰</span>
            <span className="text-lg font-bold text-red-500">{qtdSemPagamento}</span>
            <span className="text-xs text-gray-500 text-center leading-tight">Pagamentos Pendentes</span>
          </div>
        </div>
      )}

      {/* Lista */}
      <div className="flex gap-5">
        <div className="flex-1 min-w-0">
          <div className="relative mb-4">
            <div className="absolute inset-y-0 left-3 flex items-center pointer-events-none text-gray-400">
              <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-4.35-4.35M17 11A6 6 0 1 1 5 11a6 6 0 0 1 12 0z" />
              </svg>
            </div>
            <input
              className="w-full border border-gray-300 rounded-xl pl-9 pr-10 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white shadow-sm"
              placeholder="Buscar por nº OS, cliente, placa, modelo ou diagnóstico..."
              value={busca}
              onChange={e => setBusca(e.target.value)}
            />
            {busca && (
              <button onClick={() => setBusca('')} className="absolute inset-y-0 right-3 flex items-center text-gray-400 hover:text-gray-600">
                <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            )}
          </div>

          {visualizacao === 'lista' && (
            <div className="bg-white rounded-xl shadow-sm divide-y divide-gray-50">
              {servicosFiltrados.length === 0 && <p className="text-gray-400 text-center py-10">Nenhum serviço encontrado</p>}
              {servicosFiltrados.map(s => (
                <div key={s._id} className="p-4 hover:bg-gray-50 cursor-pointer transition-colors" onClick={() => navigate(`/servicos/${s._id}`, { state: { from: '/' } })}>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3 min-w-0">
                      <div className={`w-2 h-2 rounded-full flex-shrink-0 ${
                        s.status === 'concluido' ? 'bg-green-500' :
                        s.status === 'em_andamento' ? 'bg-orange-400' :
                        s.status === 'aprovado' ? 'bg-blue-500' :
                        s.status === 'aguardando_orcamento' ? 'bg-purple-500' :
                        s.status === 'aguardando_cliente' ? 'bg-cyan-500' :
                        s.status === 'cancelado' ? 'bg-red-400' : 'bg-yellow-400'
                      }`} />
                      <div className="min-w-0">
                        <div className="font-semibold text-gray-800"><span className="text-gray-400 text-sm mr-1">#{s.numero}</span>{s.cliente?.nome}</div>
                        <div className="text-sm text-gray-500 truncate">{s.veiculo?.placa} • {s.veiculo?.marca} {s.veiculo?.modelo} {s.veiculo?.ano}</div>
                        {s.responsavel && <div className="text-xs text-blue-500 font-medium">🔧 {s.responsavel}</div>}
                        <div className="text-xs text-gray-400">{format(new Date(s.updatedAt || s.createdAt), "dd/MM/yyyy", { locale: ptBR })}</div>
                      </div>
                    </div>
                    <div className="text-right flex-shrink-0 ml-3">
                      <span className={`text-xs font-medium px-2.5 py-1 rounded-full ${STATUS[s.status]?.cor}`}>{(isProprietario && s.status === 'orcamento') ? 'Orçamento em elaboração' : STATUS[s.status]?.label}</span>
                      <div className="text-sm font-semibold text-gray-700 mt-1">R$ {s.totalGeral?.toFixed(2)}</div>
                      {s.status === 'concluido' && (() => {
                        const sp = statusPagamento(s);
                        if (sp === 'pago') return <div className="text-xs font-semibold text-green-600 mt-1 whitespace-nowrap">✅ Pago</div>;
                        if (sp === 'parcial') return <div className="text-xs font-semibold text-yellow-600 mt-1 whitespace-nowrap">⚠️ Parcial</div>;
                        return <div className="text-xs font-semibold text-orange-600 mt-1 whitespace-nowrap">💰 Pagamento pendente</div>;
                      })()}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {visualizacao === 'cards' && (
            <>
              {servicosFiltrados.length === 0 && <div className="bg-white rounded-xl shadow-sm p-10 text-center text-gray-400">Nenhum serviço encontrado</div>}
              <div className={`grid ${gridClass} gap-3`}>
                {servicosFiltrados.map(s => (
                  <CardOS key={s._id} s={s} onClick={() => navigate(`/servicos/${s._id}`, { state: { from: '/' } })} isAdmin={true} />
                ))}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

export default function Dashboard() {
  const { isAdmin, usuario } = useAuth();
  return isAdmin ? <DashboardAdmin /> : <DashboardMecanico usuario={usuario} />;
}
